import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Paper,
  Typography,
  IconButton,
  Divider,
  Tabs,
  Tab,
  Grid,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableRow,
  useTheme,
  Card,
  CardContent,
  Avatar,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Speed } from "@mui/icons-material";
import { format, formatDistanceToNow } from "date-fns";
import { callAPI } from "../../functions/api";
import { re } from "mathjs";

const formatDateWithRelative = (val) => {
  if (!val) return "N/A";
  const date = new Date(val);
  return {
    relative: formatDistanceToNow(date, { addSuffix: true }),
    absolute: format(date, "yyyy-MM-dd HH:mm"),
  };
};

const severityColor = (severity) => {
  switch (severity) {
    case 4:
      return "#BA1650"; // Critical
    case 3:
      return "#F9CA00"; // High
    case 2:
      return "#E97F03"; // Medium
    case 1:
      return "#70C930"; // Low
    case 0:
      return "#888"; // Info
    default:
      return "#999"; // Unknown
  }
};

const severityLabel = (severity) => {
  switch (severity) {
    case 4:
      return "Critical";
    case 3:
      return "High";
    case 2:
      return "Medium";
    case 1:
      return "Low";
    case 0:
      return "Info";
    default:
      return "Unknown";
  }
};
const Ec2DetailsModal = ({ open, onClose, data }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [datadog, setDatadog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recommendations, setRecommendations] = useState([]);
  const theme = useTheme();
  const isLight = theme.palette.mode === "light";

  const styles = {
    container: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
      backgroundColor: isLight ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.8)",
      backdropFilter: "blur(4px)",
    },
    paper: {
      width: "92%",
      maxWidth: "1400px",
      maxHeight: "95vh",
      backgroundColor: isLight ? "#fff" : "#2a2a2a",
      color: isLight ? "#000" : "#fff",
      borderRadius: "10px",
      overflow: "hidden",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: isLight ? "#f5f5f5" : "#1f1f1f",
      px: 3,
      py: 2,
    },
    content: {
      padding: 3,
      overflowY: "auto",
      maxHeight: "calc(95vh - 100px)",
    },
    sectionTitle: {
      fontSize: "15px",
      fontWeight: "bold",
      color: isLight ? theme.palette.primary.main : "#F9CA00",
      mb: 1,
    },
    table: {
      backgroundColor: isLight ? "#fafafa" : "#1a1a1a",
      border: `1px solid ${isLight ? "#ddd" : "#444"}`,
      mb: 2,
    },
    cellLabel: {
      color: isLight ? "#333" : "#bbb",
      fontWeight: "bold",
      width: "45%",
      borderRight: `1px solid ${isLight ? "#ccc" : "#444"}`,
    },
    cellValue: {
      color: isLight ? "#000" : "#eee",
    },
    tags: {
      display: "flex",
      flexWrap: "wrap",
      gap: 1,
      maxHeight: 150,
      overflowY: "auto",
    },
    chip: {
      backgroundColor: isLight ? "#e0e0e0" : "#444",
      color: isLight ? "#000" : "#fff",
      fontSize: 11,
    },
    text: {
      fontSize: 13,
      color: isLight ? "#333" : "#ccc",
    },
    scrollable: {
      maxHeight: 400,
      overflowY: "auto",
    },
    vulnCard: {
      mb: 2,
      p: 2,
      backgroundColor: isLight ? "#f9f9f9" : "#1a1a1a",
      border: `1px solid ${isLight ? "#ccc" : "#444"}`,
    },
    vulnTitle: {
      fontWeight: "bold",
      color: isLight ? theme.palette.primary.main : "#F9CA00",
      mb: 1,
    },
    loading: {
      padding: 6,
      display: "flex",
      justifyContent: "center",
    },
    tabs: {
      backgroundColor: isLight ? "#f5f5f5" : "#1f1f1f",
      px: 2,
    },
  };

  useEffect(() => {
    if (!data) return;

    setLoading(true);

    const hasVulns =
      data.tenableVulnerabilitiesSummary?.criticalVulnerabilitiesCount > 0 ||
      data.tenableVulnerabilitiesSummary?.highVulnerabilitiesCount > 0 ||
      data.tenableVulnerabilitiesSummary?.mediumVulnerabilitiesCount > 0 ||
      data.tenableVulnerabilitiesSummary?.lowVulnerabilitiesCount > 0;

    const fetchVulns = hasVulns
      ? callAPI(`vulnerabilities/tenable/snapshotbyec2id?ec2Id=${data.id}`, "GET", null, true)
      : Promise.resolve([]);

    const fetchDatadog = callAPI(
      `logging/datadog/hostbyinstanceid?instanceId=${data.id}`,
      "GET",
      null,
      true
    );

    const fetchRecommendations = callAPI(
      `Insights/computeOptimizerRecommendationByInstanceId?instanceId=${data.id}`,
      "GET",
      null,
      true
    );

    Promise.allSettled([fetchVulns, fetchDatadog, fetchRecommendations])
      .then(([vulnResult, ddResult, recResult]) => {
        if (vulnResult.status === "fulfilled") {
          setVulnerabilities(vulnResult.value || []);
        } else {
          console.error("Vulnerability fetch failed:", vulnResult.reason);
        }

        if (ddResult.status === "fulfilled") {
          setDatadog(ddResult.value || null);
        } else {
          console.error("Datadog fetch failed:", ddResult.reason);
        }

        if (recResult.status === "fulfilled") {
          // The API returns a single recommendation object, wrap it in an array
          const recData = recResult.value;
          if (recData && recData.recommendations && recData.recommendations.length > 0) {
            setRecommendations([recData]); // Wrap the entire object in an array
          } else {
            setRecommendations([]);
          }
        } else {
          console.error("Recommendations fetch failed:", recResult.reason);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [data]);

  if (!data) return null;

  const tags = data.tags || {};
  const projectName = tags["Project Name"] || "-";
  const serviceOwner = tags["Service Owner"] || "-";

  const formatDate = (val) => (val ? format(new Date(val), "yyyy-MM-dd HH:mm:ss") : "N/A");

  const envColor = (data.environment || "").toLowerCase().includes("prod")
    ? "#BA1650"
    : (data.environment || "").toLowerCase().includes("dev")
      ? "#70C930"
      : "#F9CA00";

  const section = (icon, title, rows) => (
    <Box sx={{ mb: 1 }}>
      <Typography sx={styles.sectionTitle}>
        {icon} {title}
      </Typography>
      <Table size="small" sx={styles.table}>
        <TableBody>
          {rows.map(([label, value], i) => (
            <TableRow key={i}>
              <TableCell sx={styles.cellLabel}>{label}</TableCell>
              <TableCell sx={styles.cellValue}>{value ?? "N/A"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );

  const renderTags = () => (
    <Box sx={styles.tags}>
      {Object.keys(tags).length > 0 ? (
        Object.entries(tags).map(([key, val], i) => (
          <Chip key={i} label={`${key}: ${val}`} size="small" sx={styles.chip} />
        ))
      ) : (
        <Typography sx={styles.text}>No tags assigned</Typography>
      )}
    </Box>
  );

  const renderVulnerabilities = () =>
    vulnerabilities.length === 0 ? (
      <Typography sx={styles.text}>No vulnerabilities found.</Typography>
    ) : (
      <Box sx={styles.scrollable}>
        {vulnerabilities.map((v, i) => {
          const firstSeen = formatDateWithRelative(v.firstFound);
          const lastSeen = formatDateWithRelative(v.lastFound);

          return (
            <Paper key={i} sx={styles.vulnCard}>
              <Typography sx={styles.vulnTitle}>🔐 {v.synopsis}</Typography>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 1 }}>
                <Chip
                  label={`Severity: ${severityLabel(v.severity)}`}
                  sx={{ backgroundColor: severityColor(v.severity), color: "#fff" }}
                />

                <Chip
                  label={`CVSS: ${v.cvss3BaseScore} / ${v.cvss3TemporalScore}`}
                  sx={{ backgroundColor: "#555", color: "#fff" }}
                />
                <Chip
                  label={`Plugin ID: ${v.pluginId}`}
                  sx={{ backgroundColor: "#444", color: "#ddd" }}
                />
              </Box>

              <Typography sx={{ color: "#F9CA00", fontWeight: "bold" }}>🕒 First Seen</Typography>
              <Typography sx={styles.text}>
                {firstSeen.relative}
                <br />
                <small style={{ color: "#888" }}>{firstSeen.absolute}</small>
              </Typography>

              <Typography sx={{ color: "#F9CA00", fontWeight: "bold", mt: 1 }}>
                🕒 Last Seen
              </Typography>
              <Typography sx={styles.text}>
                {lastSeen.relative}
                <br />
                <small style={{ color: "#888" }}>{lastSeen.absolute}</small>
              </Typography>

              <Typography sx={{ color: "#F9CA00", fontWeight: "bold", mt: 2 }}>
                📝 Description
              </Typography>
              <Typography sx={styles.text}>{v.pluginDescription}</Typography>

              <Typography sx={{ color: "#F9CA00", fontWeight: "bold", mt: 2 }}>
                💡 Solution
              </Typography>
              <Typography sx={styles.text}>{v.solution}</Typography>

              {v.pluginOutput && (
                <>
                  <Typography sx={{ color: "#F9CA00", fontWeight: "bold", mt: 2 }}>
                    🔍 Plugin Output
                  </Typography>
                  <Typography sx={styles.text} whiteSpace="pre-wrap">
                    {v.pluginOutput}
                  </Typography>
                </>
              )}
            </Paper>
          );
        })}
      </Box>
    );

  const renderDatadog = () => {
    if (!datadog || (datadog.value && datadog.value == null)) {
      return <Typography sx={styles.text}>No Datadog data found.</Typography>;
    }

    return (
      <Grid container spacing={2}>
        <Grid item xs={4}>
          {section("📈", "Usage Metrics", [
            ["CPU Usage", datadog.cpuUtilization != null ? `${datadog.cpuUtilization}%` : "N/A"],
            [
              "Memory Usage",
              datadog.memoryUtilization != null ? `${datadog.memoryUtilization}%` : "N/A",
            ],
            ["Disk Usage", datadog.diskUtilization != null ? `${datadog.diskUtilization}%` : "N/A"],
          ])}
        </Grid>
        <Grid item xs={4}>
          {section("💾", "Capacity", [
            ["Memory Total", datadog.memoryTotal != null ? `${datadog.memoryTotal} MB` : "N/A"],
            ["Disk Total", datadog.diskTotal != null ? `${datadog.diskTotal} GB` : "N/A"],
          ])}
        </Grid>
        <Grid item xs={4}>
          {section("🕒", "Status", [
            ["Last Updated", datadog.lastUpdated ? formatDate(datadog.lastUpdated) : "N/A"],
            ["Status", datadog.status || "N/A"],
            ["Muted", datadog.isMuted ? "Yes" : "No"],
          ])}
        </Grid>
        <Grid item xs={4}>
          {section("🌍", "Cloud Metadata", [
            ["AWS Name", datadog.awsName || "N/A"],
            ["AWS Account", datadog.awsAccountName || "N/A"],
            ["Region", datadog.region || "N/A"],
            ["AZ", datadog.availabilityZone || "N/A"],
            ["Business Unit", datadog.businessUnit || "N/A"],
            ["Service Owner", datadog.serviceOwner || "N/A"],
            ["Cost Center", datadog.costCenter || "N/A"],
          ])}
        </Grid>
        <Grid item xs={4}>
          {section("🔧", "Identifiers", [
            ["Datadog ID", datadog.id],
            ["AWS Instance ID", datadog.awsId],
            ["Name", datadog.name || "N/A"],
            ["Host Name", datadog.hostName || "N/A"],
          ])}
        </Grid>

        <Grid item xs={4}>
          {section("🧩", "Platform Info", [
            ["Platform", datadog.platform || "N/A"],
            ["OS", datadog.os || "N/A"],
            ["Instance Type", datadog.instanceType || "N/A"],
          ])}
        </Grid>
      </Grid>
    );
  };

  const renderRecommendations = () => {
    if (!recommendations || recommendations.length === 0) {
      return <Typography sx={styles.text}>No optimization recommendations available.</Typography>;
    }

    return (
      <Box sx={styles.scrollable}>
        {recommendations.map((rec, index) => {
          const bestOption = rec.recommendations?.[0];
          const savingsValue = bestOption?.savingOpportunity?.estimatedMonthlySavingsValue || 0;
          const savingsPercent = bestOption?.savingOpportunity?.savingsOpportunityPercentage || 0;
          const performanceRisk = bestOption?.performanceRisk || 3;

          const riskLevel =
            performanceRisk === 1 ? "low" : performanceRisk === 2 ? "medium" : "high";
          const riskColor =
            performanceRisk === 1 ? "#4caf50" : performanceRisk === 2 ? "#ff9800" : "#f44336";

          // Priority color based on savings and risk
          let priorityScore = 0;
          if (savingsValue > 500) priorityScore += 3;
          else if (savingsValue > 200) priorityScore += 2;
          else priorityScore += 1;
          if (performanceRisk === 1) priorityScore += 2;
          else if (performanceRisk === 2) priorityScore += 1;

          const priorityColor =
            priorityScore >= 5 ? "#f44336" : priorityScore >= 3 ? "#ff9800" : "#4caf50";

          // Get utilization metrics and build resource metrics dynamically
          const utilizationMetrics = rec.utilizationMetrics || [];
          const resourceMetrics = {};

          // Define metric configurations with display properties
          const metricConfigs = {
            Cpu: {
              name: "CPU",
              color: "#ff9800",
              unit: "%",
              maxValue: 100,
              divisor: 1,
              decimals: 1,
            },
            Memory: {
              name: "Memory",
              color: "#4caf50",
              unit: "%",
              maxValue: 100,
              divisor: 1,
              decimals: 1,
            },
            NETWORK_IN_BYTES_PER_SECOND: {
              name: "Network In",
              color: "#2196f3",
              unit: " MB/s",
              maxValue: 1250,
              divisor: 1000000,
              decimals: 1,
            },
            NETWORK_OUT_BYTES_PER_SECOND: {
              name: "Network Out",
              color: "#03a9f4",
              unit: " MB/s",
              maxValue: 1250,
              divisor: 1000000,
              decimals: 1,
            },
            EBS_READ_BYTES_PER_SECOND: {
              name: "EBS Read",
              color: "#9c27b0",
              unit: " MB/s",
              maxValue: 500,
              divisor: 1000000,
              decimals: 1,
            },
            EBS_WRITE_BYTES_PER_SECOND: {
              name: "EBS Write",
              color: "#673ab7",
              unit: " MB/s",
              maxValue: 500,
              divisor: 1000000,
              decimals: 1,
            },
            EBS_READ_OPS_PER_SECOND: {
              name: "EBS Read Ops",
              color: "#e91e63",
              unit: " IOPS",
              maxValue: 1000,
              divisor: 1,
              decimals: 0,
            },
            EBS_WRITE_OPS_PER_SECOND: {
              name: "EBS Write Ops",
              color: "#f06292",
              unit: " IOPS",
              maxValue: 1000,
              divisor: 1,
              decimals: 0,
            },
            NETWORK_PACKETS_IN_PER_SECOND: {
              name: "Packets In",
              color: "#00bcd4",
              unit: " pps",
              maxValue: 10000,
              divisor: 1,
              decimals: 0,
            },
            NETWORK_PACKETS_OUT_PER_SECOND: {
              name: "Packets Out",
              color: "#26c6da",
              unit: " pps",
              maxValue: 10000,
              divisor: 1,
              decimals: 0,
            },
          };

          // Process each utilization metric
          utilizationMetrics.forEach((metric) => {
            const config = metricConfigs[metric.name];
            if (config && metric.value > 0) {
              const displayValue = metric.value / config.divisor;
              const barWidth = Math.min((displayValue / config.maxValue) * 100, 100);

              // Only show metrics with meaningful values (not rounding to 0)
              const formattedValue = displayValue.toFixed(config.decimals);
              if (parseFloat(formattedValue) > 0) {
                resourceMetrics[metric.name] = {
                  value: displayValue,
                  label: `${formattedValue}${config.unit}`,
                  barWidth: barWidth,
                  color: config.color,
                  name: config.name,
                };
              }
            }
          });

          return (
            <Card
              key={index}
              sx={{
                mb: 2,
                background: isLight
                  ? `linear-gradient(135deg, ${alpha(priorityColor, 0.05)} 0%, transparent 50%)`
                  : `linear-gradient(135deg, ${alpha(priorityColor, 0.1)} 0%, transparent 50%)`,
                border: `1px solid ${alpha(priorityColor, 0.2)}`,
                borderLeft: `4px solid ${priorityColor}`,
                transition: "all 0.3s ease",
                overflow: "hidden",
              }}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 0 } }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(priorityColor, 0.1),
                      color: priorityColor,
                      width: 40,
                      height: 40,
                    }}
                  >
                    <Speed />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0, pb: 1 }}>
                    {/* Title */}
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                      {rec.currentInstanceType} → {bestOption?.instanceType || "Optimize"}
                    </Typography>

                    {/* Description */}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1.5, lineHeight: 1.4 }}
                    >
                      Optimization opportunity detected
                    </Typography>

                    {/* Bottom section - savings and metrics */}
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        flexDirection: "row",
                        overflow: "hidden",
                        alignItems: "flex-start",
                      }}
                    >
                      {/* Left side - Cost savings */}
                      <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                        <Box sx={{ mb: 0.5 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.7rem", mb: 0.5, display: "block" }}
                          >
                            Potential Cost Saving
                          </Typography>
                          <Typography
                            variant="h5"
                            sx={{ color: priorityColor, fontWeight: 700, fontSize: "1.5rem" }}
                          >
                            ${savingsValue.toFixed(2)}
                            <Typography
                              component="span"
                              sx={{ fontSize: "0.9rem", fontWeight: 500, ml: 0.5 }}
                            >
                              /mo
                            </Typography>
                          </Typography>
                        </Box>

                        {bestOption && (
                          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, mt: 1 }}>
                            💡 Downsize to {bestOption.instanceType} ({savingsPercent.toFixed(1)}%
                            savings)
                          </Typography>
                        )}
                        {riskLevel && (
                          <Typography variant="caption" color="text.secondary">
                            ⚠️ Performance risk: {riskLevel}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                  {/* Right side - Vertical resource bars */}
                  {Object.keys(resourceMetrics).length > 0 && (
                    <Box
                      sx={{
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 1.5,
                        height: "100%",
                        bgcolor: alpha(theme.palette.background.paper, 0.5),
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.7rem", fontWeight: 600, mb: 1, display: "block" }}
                      >
                        Key utilization Metrics
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1.5,
                          alignItems: "flex-start",
                          justifyContent: "flex-end",
                        }}
                      >
                        {Object.entries(resourceMetrics).map(([key, metric]) => (
                          <Box
                            key={key}
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 0.3,
                            }}
                          >
                            <Box
                              sx={{
                                width: 40,
                                height: "100%",
                                minHeight: 100,
                                bgcolor: alpha(metric.color, 0.2),
                                borderRadius: 1,
                                overflow: "hidden",
                                display: "flex",
                                flexDirection: "column-reverse",
                                position: "relative",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Box
                                sx={{
                                  height: `${Math.min(metric.barWidth, 100)}%`,
                                  width: "100%",
                                  bgcolor: metric.color,
                                  transition: "height 0.3s ease",
                                  position: "absolute",
                                  bottom: 0,
                                  left: 0,
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.65rem",
                                  fontWeight: 700,
                                  color: "#fff",
                                  writingMode: "vertical-rl",
                                  transform: "rotate(180deg)",
                                  whiteSpace: "nowrap",
                                  position: "relative",
                                  zIndex: 1,
                                  textShadow: "0 1px 3px rgba(0,0,0,0.5)",
                                }}
                              >
                                {metric.label}
                              </Typography>
                            </Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.65rem", textAlign: "center" }}
                            >
                              {metric.name}
                            </Typography>
                          </Box>
                        ))}
                      </Box>{" "}
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={styles.container}>
        <Paper sx={styles.paper}>
          <Box sx={styles.header}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: "#F9CA00" }}>
              EC2 Instance Details
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon sx={{ color: "#fff" }} />
            </IconButton>
          </Box>

          {loading ? (
            <Box sx={styles.loading}>
              <CircularProgress sx={{ color: "#F9CA00" }} />
            </Box>
          ) : (
            <>
              <Tabs
                value={activeTab}
                onChange={(e, newTab) => setActiveTab(newTab)}
                textColor="inherit"
                indicatorColor="secondary"
                sx={styles.tabs}
              >
                <Tab label="Overview" />
                <Tab
                  label={`Vulnerabilities${vulnerabilities.length ? ` (${vulnerabilities.length})` : ""}`}
                  disabled={!vulnerabilities.length}
                />
                <Tab
                  label="Datadog Metrics"
                  disabled={!datadog || (datadog.value && datadog.value == null)}
                />
                <Tab
                  label={`Recommendations${recommendations.length ? ` (${recommendations.length})` : ""}`}
                  disabled={!recommendations.length}
                />
              </Tabs>
              <Divider sx={{ backgroundColor: "#444" }} />

              <Box sx={styles.content}>
                {activeTab === 0 && (
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      {section("🔹", "Instance Information", [
                        ["Instance ID", data.id],
                        ["Name", data.name],
                        ["Project Name", projectName],
                        ["Service Owner", serviceOwner],
                        [
                          "Environment",
                          <Chip
                            label={data.environment}
                            size="small"
                            sx={{ backgroundColor: envColor, color: "#000" }}
                          />,
                        ],
                        ["OS", `${data.osName ?? "-"} ${data.osVersion ?? ""}`],
                      ])}
                    </Grid>
                    <Grid item xs={4}>
                      {section("📜", "Account Details", [
                        ["AWS Account", data.accountName],
                        ["Account Type", "Linked"],
                        ["Account Status", "active"],
                        ["Region", data.regionDisplayName],
                      ])}
                    </Grid>
                    <Grid item xs={4}>
                      {section("📜", "Tenable Details", [
                        ["Has Tenable Agent", data.hasTenableAgent ? "Yes" : "No"],
                        ["Has Tenable License", data.tenableIsLicensed ? "Yes" : "No"],
                        [
                          "Last Scan Date",
                          (() => {
                            const scan = formatDateWithRelative(data.tenableLastScan);
                            return (
                              <>
                                <span>{scan.relative}</span>
                                <br />
                                <span style={{ fontSize: "11px", color: "#888" }}>
                                  {scan.absolute}
                                </span>
                              </>
                            );
                          })(),
                        ],
                      ])}
                    </Grid>

                    <Grid item xs={4}>
                      {section("💰", "Costs", [
                        ["Monthly Cost", "pending.."],
                        ["Hourly Cost", "pending.."],
                        ["Projected Cost", "pending.."],
                      ])}
                    </Grid>
                    <Grid item xs={4}>
                      {section("🛠", "Configuration", [
                        ["Root Device", data.image_root_device_type],
                        ["Architecture", data.architecture],
                        ["Virtualization", data.virtualization_type],
                      ])}
                    </Grid>
                    <Grid item xs={4}>
                      {section("🧠", "Orchestration", [
                        ["Created At", formatDate(data.launchTime)],
                        ["Updated At", formatDate(data.lastUpdated)],
                        ["Type", data.instanceType],
                      ])}
                    </Grid>

                    <Grid item xs={6}>
                      {section("🌐", "Networking", [
                        ["IP, Private IP", `${data.publicDnsName}, ${data.privateDnsName}`],
                        ["DNS", data.publicDnsName],
                        ["VPC ID", "vpc-f13e008a"],
                      ])}
                    </Grid>

                    <Grid item xs={6}>
                      <Typography sx={styles.sectionTitle}>🏷️ Tags</Typography>
                      {renderTags()}
                    </Grid>
                  </Grid>
                )}
                {activeTab === 1 && renderVulnerabilities()}
                {activeTab === 2 && renderDatadog()}
                {activeTab === 3 && renderRecommendations()}
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Modal>
  );
};

export default Ec2DetailsModal;
